// Shared catalog inspector.
//
// Started life as a part-only pair (ProductIdLink + PartDetailModal) extracted
// verbatim from SalesOrderPage (2026-08-19) so the Fabrication job queue's
// cutting-task table could reuse it.
//
// Generalised 2026-09-02: a Sales Order's Line Items table shows three kinds of
// row and only one of them could be inspected. Assembly group headers had no
// link at all, and a purchased component row carries `consumable_id` with a
// NULL `part_id`, so it fell through ProductIdLink's guard to a plain <strong>.
// CatalogItemLink/CatalogItemDetailModal take a {kind, id} descriptor instead of
// a bare part id and know how to fetch all three. ProductIdLink and
// PartDetailModal are kept as thin part-only wrappers — every existing call site
// still passes a bare part_id and is untouched.
import React, { useState, useEffect, useCallback } from 'react';

// kind -> GET path under apiBase. The id is always the primary key of that
// catalog table (parts.part_id / consumables.consumable_id / assemblies.assembly_id).
const ENDPOINT = {
  part: (id) => `parts/${id}`,
  consumable: (id) => `consumables/${id}`,
  assembly: (id) => `assemblies/${id}`,
};

const KIND_LABEL = { part: 'Part', consumable: 'Component', assembly: 'Assembly' };

const KIND_BADGE = {
  part: null, // a part is the default; its Rev badge carries the useful signal
  consumable: { label: 'Component', cls: 'bg-info' },
  assembly: { label: 'Assembly', cls: 'bg-primary' },
};

const isItem = (item) => !!(item && item.kind && ENDPOINT[item.kind] && item.id != null);
// Stable dependency for the fetch effect — an object literal prop would refetch
// on every parent render.
const itemKey = (item) => (isItem(item) ? `${item.kind}:${item.id}` : null);

// A clickable catalog id. `item` is a {kind, id} descriptor; onInspect(item) is
// called with it. Falls back to a bold span when no handler / no item is
// available so render sites can pass it freely without guarding.
//
// The clickable form keeps `text-decoration-none` (a permanently underlined id
// in a dense table is noise) but carries `catalog-id-link`, which underlines on
// hover/focus — src/index.css. Without that a caller-supplied muted className
// made a live button indistinguishable from static text.
// `style` is merged over the defaults: a caller whose id sits in larger text
// (the Surplus Rack's "5 pcs FAB311162" heading) passes { fontSize: 'inherit' }.
const CatalogItemLink = ({ productId, item, onInspect, className = '', title, style }) => {
  if (!productId) return <span className="text-muted">—</span>;
  if (!isItem(item) || typeof onInspect !== 'function') {
    return <strong className={className} style={style}>{productId}</strong>;
  }
  return (
    <button
      type="button"
      className={`btn btn-link btn-sm p-0 fw-bold text-decoration-none align-baseline catalog-id-link ${className}`}
      style={{ lineHeight: 1, ...style }}
      title={title || `View details for ${productId}`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onInspect(item); }}
    >
      {productId}
    </button>
  );
};

// Part-only wrapper — preserves the original (productId, partId, onInspect)
// signature used by ~15 call sites across SalesOrderPage and JobQueuePage.
const ProductIdLink = ({ productId, partId, onInspect, className = '' }) => (
  <CatalogItemLink
    productId={productId}
    item={partId ? { kind: 'part', id: partId } : null}
    onInspect={typeof onInspect === 'function' ? (it) => onInspect(it.id) : null}
    className={className}
  />
);

const fetchJson = (url) =>
  fetch(url).then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))));

// Modal that fetches the item and renders its key fields + image. Opens
// whenever `item` is a valid descriptor. Lazy — fetches only when the item
// changes. Esc + click-outside both close.
//
// Drilling in from an assembly's component list pushes onto an internal trail,
// so the Back button returns to the assembly instead of dead-ending. The trail
// resets whenever the parent opens a different item.
const CatalogItemDetailModal = ({ apiBase, item, onClose }) => {
  const openKey = itemKey(item);
  const [trail, setTrail] = useState([]);
  const [current, setCurrent] = useState(item);
  const [data, setData] = useState(null);
  // Assembly children (parts + consumables); empty for the other kinds.
  const [children, setChildren] = useState([]);
  const [enlarged, setEnlarged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // A new item from the parent starts a fresh trail.
  useEffect(() => {
    setCurrent(item);
    setTrail([]);
    setEnlarged(false);
    // openKey identifies the item; `item` itself is a fresh literal each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  const drillTo = useCallback((next) => {
    if (!isItem(next)) return;
    setTrail((t) => (isItem(current) ? [...t, current] : t));
    setCurrent(next);
    setEnlarged(false);
  }, [current]);

  const goBack = useCallback(() => {
    setTrail((t) => {
      if (t.length === 0) return t;
      setCurrent(t[t.length - 1]);
      setEnlarged(false);
      return t.slice(0, -1);
    });
  }, []);

  const currentKey = itemKey(current);
  const kind = current?.kind;

  useEffect(() => {
    if (!currentKey) return;
    const [k, rawId] = currentKey.split(':');
    let cancelled = false;
    setLoading(true); setErr(''); setData(null); setChildren([]);

    fetchJson(`${apiBase}/${ENDPOINT[k](rawId)}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(e.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // An assembly's whole point is what is inside it. Best-effort: a failure
    // here must not blank the assembly's own fields.
    if (k === 'assembly') {
      Promise.all([
        fetchJson(`${apiBase}/assemblies/${rawId}/components`).catch(() => []),
        fetchJson(`${apiBase}/assemblies/${rawId}/consumable-components`).catch(() => []),
      ]).then(([parts, consumables]) => {
        if (cancelled) return;
        setChildren([
          ...(Array.isArray(parts) ? parts : []).map((c) => ({
            key: `p-${c.assembly_component_id ?? c.part_id}`,
            item: c.part_id ? { kind: 'part', id: c.part_id } : null,
            product_id: c.product_id,
            description: c.description,
            qty: c.quantity_per_assembly,
            revision_code: c.revision_code,
          })),
          ...(Array.isArray(consumables) ? consumables : []).map((c) => ({
            key: `c-${c.assembly_consumable_id ?? c.consumable_id}`,
            item: c.consumable_id ? { kind: 'consumable', id: c.consumable_id } : null,
            product_id: c.product_id,
            description: c.description,
            qty: c.quantity_per_assembly,
            purchased: true,
          })),
        ]);
      });
    }
    return () => { cancelled = true; };
  }, [apiBase, currentKey]);

  // Close on Esc
  useEffect(() => {
    if (!currentKey) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Collapse the enlarged image first; a second Esc closes the modal.
      // (onClose is called here, never inside a setEnlarged updater: an updater
      // runs while rendering, and the parent's setState there is a React warning.)
      if (enlarged) setEnlarged(false);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentKey, onClose, enlarged]);

  if (!currentKey) return null;

  const field = (label, value) => {
    if (value === null || value === undefined || value === '') return null;
    return (
      <React.Fragment key={label}>
        <dt className="col-5 text-muted">{label}</dt>
        <dd className="col-7 mb-1">{value}</dd>
      </React.Fragment>
    );
  };

  const money = (v) => (v === null || v === undefined || v === '' ? null : `$${(+v).toFixed(2)}`);
  const imagePath = data ? (data.image_path || data.image_url || null) : null;
  const notes = data ? (data.notes || null) : null;
  const badge = KIND_BADGE[kind];

  const heading = data
    ? (kind === 'assembly' ? data.assembly_name : data.product_id)
    : (loading ? 'Loading…' : 'Details');

  const partFields = () => (
    <dl className="row small mb-0">
      {field('Description', data.description)}
      {field('Customer part', data.customer_drawing_id)}
      {field('Size (L × W)', (data.size_length || data.size_width)
        ? `${data.size_length ?? '—'}″ × ${data.size_width ?? '—'}″` : null)}
      {field('Thickness',
        data.display_thickness
          ? `${data.display_thickness}${data.thickness_in ? ` (${data.thickness_in}″)` : ''}`
          : null)}
      {field('Material', data.material_type)}
      {field('Finish', data.surface_finish)}
      {field('Weight', data.weight_lbs != null ? `${data.weight_lbs} lb` : null)}
      {field('Process', data.process_steps)}
      {field('Coating color', data.coating_color_code
        ? `${data.coating_color_code}${data.coating_color_name ? ` — ${data.coating_color_name}` : ''}`
        : null)}
      {field('Stock type', data.stock_type)}
      {field('Price', money(data.price))}
      {field('Revision', data.revision_code
        ? `${data.revision_code}${data.is_current_revision === false ? ' (superseded)' : ''}` : null)}
      {field('Part ID', `#${data.part_id}`)}
    </dl>
  );

  const consumableFields = () => (
    <dl className="row small mb-0">
      {field('Description', data.description)}
      {field('Category', data.category)}
      {field('Type', data.consumable_type)}
      {field('Price', money(data.price))}
      {field('Weight', data.weight_lbs != null ? `${data.weight_lbs} lb` : null)}
      {field('On hand', data.stock_quantity != null ? `${data.stock_quantity}` : null)}
      {field('Reorder point', data.reorder_point != null ? `${data.reorder_point}` : null)}
      {field('Min order qty', data.min_order_qty != null ? `${data.min_order_qty}` : null)}
      {field('Order in multiples of', data.order_in_qty_of != null ? `${data.order_in_qty_of}` : null)}
      {field('Consumable ID', `#${data.consumable_id}`)}
    </dl>
  );

  const assemblyFields = () => (
    <dl className="row small mb-0">
      {field('Primary part', data.primary_part ? (
        <CatalogItemLink
          productId={data.primary_part.product_id}
          item={{ kind: 'part', id: data.primary_part.part_id }}
          onInspect={drillTo}
        />
      ) : null)}
      {field('Primary per set', data.primary_part_quantity != null ? `${data.primary_part_quantity}` : null)}
      {field('Build set qty', data.build_set_quantity != null ? `${data.build_set_quantity}` : null)}
      {field('Join workstation', data.join_workstation)}
      {field('Price', data.assembly_price != null
        ? `${money(data.assembly_price)}/set${data.assembly_price_source ? ` (${data.assembly_price_source})` : ''}`
        : null)}
      {field('Ship size (L × W × H)', (data.ship_length_in || data.ship_width_in || data.ship_height_in)
        ? `${data.ship_length_in ?? '—'}″ × ${data.ship_width_in ?? '—'}″ × ${data.ship_height_in ?? '—'}″` : null)}
      {field('Ship weight', data.ship_weight_lbs != null ? `${data.ship_weight_lbs} lb` : null)}
      {field('Stackable', data.ship_stackable === true
        ? `Yes${data.ship_max_stack_layers ? ` — max ${data.ship_max_stack_layers} layers` : ''}`
        : (data.ship_stackable === false ? 'No' : null))}
      {field('Assembly ID', `#${data.assembly_id}`)}
    </dl>
  );

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-label={`${KIND_LABEL[kind] || 'Item'} details`}
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1055 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header py-2">
            <h5 className="modal-title">
              {heading}
              {badge && (
                <span className={`badge ${badge.cls} ms-2`} style={{ fontSize: '0.7em' }}>{badge.label}</span>
              )}
              {kind === 'part' && data && data.revision_code && (
                <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7em' }}>
                  Rev {data.revision_code}
                </span>
              )}
              {kind === 'part' && data && data.flagged && (
                <span className="badge bg-danger ms-2" style={{ fontSize: '0.7em' }}>Flagged</span>
              )}
            </h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
          </div>
          <div className="modal-body py-3">
            {loading && (
              <div className="text-muted">
                <span className="spinner-border spinner-border-sm me-2"></span>Loading {kind} #{current.id}…
              </div>
            )}
            {err && <div className="alert alert-danger small mb-0">{err}</div>}
            {data && (
              <div className="row g-3">
                <div className="col-md-5">
                  {imagePath ? (
                    <img
                      src={imagePath}
                      alt={heading}
                      title="Click to enlarge"
                      style={{ width: '100%', border: '1px solid #ddd', borderRadius: 4, background: '#fafafa', cursor: 'zoom-in' }}
                      onClick={() => setEnlarged(true)}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="text-muted small border rounded p-4 text-center">
                      <i className="bi bi-image me-1"></i>No image
                    </div>
                  )}
                  {notes && (
                    <div className="small mt-2 p-2 bg-light rounded">
                      <i className="bi bi-sticky me-1"></i>
                      {notes}
                    </div>
                  )}
                </div>
                <div className="col-md-7">
                  {kind === 'part' && partFields()}
                  {kind === 'consumable' && consumableFields()}
                  {kind === 'assembly' && assemblyFields()}
                </div>
              </div>
            )}
            {data && kind === 'assembly' && children.length > 0 && (
              <div className="mt-3">
                <div className="small fw-bold text-muted mb-1">
                  <i className="bi bi-diagram-3 me-1"></i>
                  Components ({children.length}) — click one to inspect it
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover small mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '140px' }}>Product ID</th>
                        <th className="text-center" style={{ width: '70px' }}>Qty/set</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {children.map((c) => (
                        <tr key={c.key}>
                          <td>
                            {c.purchased && <i className="bi bi-cart-fill me-1 text-info" title="Purchased Item"></i>}
                            <CatalogItemLink productId={c.product_id} item={c.item} onInspect={drillTo} />
                            {c.revision_code && <span className="text-muted ms-2">Rev {c.revision_code}</span>}
                          </td>
                          <td className="text-center">{c.qty ?? '—'}</td>
                          <td className="text-muted">{c.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer py-2">
            {trail.length > 0 && (
              <button type="button" className="btn btn-sm btn-outline-secondary me-auto" onClick={goBack}>
                <i className="bi bi-arrow-left me-1"></i>Back
              </button>
            )}
            {/* Only parts have a deep-linkable page; /assemblies and /consumables
                are unfiltered list routes, so linking there would lie. */}
            {kind === 'part' && data && data.part_id && (
              <a
                className={`btn btn-sm btn-outline-secondary ${trail.length > 0 ? '' : 'me-auto'}`}
                href={`/parts/${data.part_id}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open full part page in a new tab"
              >
                <i className="bi bi-box-arrow-up-right me-1"></i>Open part page
              </a>
            )}
            <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
      {enlarged && imagePath && (
        <div
          onClick={() => setEnlarged(false)}
          title="Click to close"
          style={{
            position: 'fixed', inset: 0, zIndex: 1070,
            background: 'rgba(0,0,0,0.82)', cursor: 'zoom-out',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '2vh 2vw',
          }}
        >
          <img
            src={imagePath}
            alt={heading}
            style={{ maxWidth: '96vw', maxHeight: '96vh', width: 'auto', height: 'auto',
                     background: '#fff', borderRadius: 6 }}
          />
        </div>
      )}
    </div>
  );
};

// Part-only wrapper — preserves the original (apiBase, partId, onClose) signature.
const PartDetailModal = ({ apiBase, partId, onClose }) => (
  <CatalogItemDetailModal
    apiBase={apiBase}
    item={partId ? { kind: 'part', id: partId } : null}
    onClose={onClose}
  />
);

export { ProductIdLink, PartDetailModal, CatalogItemLink, CatalogItemDetailModal };
